import { FlaskConical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { KeywordTrend } from "@/components/ui/keyword-trend";
import { RankingCell } from "@/components/keyword-landscape-output/RankingCell";
import { TableBody, TableCell, TableHead, TableHeader } from "@/components/ui/table";
import {
  NestedDrawerTableBodyRow,
  NestedDrawerTableContainer,
  NestedDrawerTableHeaderRow,
} from "@/components/shared/NestedDrawerTable";
import type { SeoStrategyRecommendation } from "@/types/seo-strategy";

interface StrategyOpportunitiesListProps {
  recommendation: SeoStrategyRecommendation;
  /** Absent everywhere the parent has nothing to start a test from (e.g. a `viewer`). */
  onStartTest?: (keyword?: string) => void;
}

const number = (value: number | null) => (value === null ? "—" : value.toLocaleString());

/**
 * The ticket's "Opportunities list" -- today's `RecommendedTestsTable`, stripped of the
 * Status, Result, Action and Type columns and every per-row button (intent FR-19, FR-20).
 * Absent entirely on a dismissed card; the caller decides that, not this component.
 */
export function StrategyOpportunitiesList({ recommendation, onStartTest }: StrategyOpportunitiesListProps) {
  const grouping = recommendation.thematicGrouping;
  const keywords = grouping?.keywords ?? [];

  return (
    <div className="space-y-3 border-t border-border pt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h5 className="text-base font-semibold leading-6 text-foreground">Recommended Tests</h5>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Keywords worth testing to close this gap. Signal AI scans prior A/B tests and flags any
            that already ran against these terms.
          </p>
        </div>

        {onStartTest ? (
          <Button size="sm" className="shrink-0 gap-1" onClick={() => onStartTest()}>
            <FlaskConical className="h-4 w-4" />
            {recommendation.tests.length > 0 ? "Start test" : "Start first test"}
          </Button>
        ) : null}
      </div>

      {keywords.length === 0 ? (
        <p className="text-sm text-muted-foreground">No keywords resolved for this gap yet.</p>
      ) : (
        <NestedDrawerTableContainer className="ml-0 border-b-0 px-0 before:hidden">
          <TableHeader>
            <NestedDrawerTableHeaderRow flush>
              <TableHead>Keyword</TableHead>
              <TableHead className="min-w-[180px] whitespace-nowrap">Theme</TableHead>
              <TableHead className="text-right">Hero Rank</TableHead>
              <TableHead className="text-right">KPS</TableHead>
              <TableHead className="text-right">Search Vol.</TableHead>
              <TableHead className="text-right">Comp.</TableHead>
              <TableHead className="text-right">Rel.</TableHead>
              <TableHead className="text-right">Trend</TableHead>
            </NestedDrawerTableHeaderRow>
          </TableHeader>
          <TableBody>
            {keywords.map((keyword) => (
              <NestedDrawerTableBodyRow key={keyword.keyword} flush>
                <TableCell className="font-medium">{keyword.keyword}</TableCell>
                <TableCell className="whitespace-nowrap">
                  <Badge className="border-0 bg-secondary text-foreground">{grouping?.name}</Badge>
                </TableCell>
                {/* AUT-296: the same cell the Keywords tab renders, rather than a
                    lookalike -- so "N/A", the pending label and the rank pill's colour
                    bands can't drift apart between the two screens. */}
                <TableCell className="text-right">
                  <RankingCell rank={keyword.heroRank} />
                </TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{number(keyword.kps)}</TableCell>
                <TableCell className="text-right tabular-nums">{number(keyword.searchVolume)}</TableCell>
                <TableCell className="text-right tabular-nums">{number(keyword.competitors)}</TableCell>
                <TableCell className="text-right tabular-nums">{number(keyword.relevance)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  <KeywordTrend value={keyword.trend} />
                </TableCell>
              </NestedDrawerTableBodyRow>
            ))}
          </TableBody>
        </NestedDrawerTableContainer>
      )}
    </div>
  );
}
